/**
 * Telegram-style album layout algorithm
 * Based on https://github.com/Ajaxy/telegram-tt
 */

export interface MediaDimensions {
  width: number;
  height: number;
  x: number;
  y: number;
}

export interface MediaLayout {
  dimensions: MediaDimensions;
}

export interface AlbumLayout {
  layout: MediaLayout[];
  containerWidth: number;
  containerHeight: number;
}

interface LayoutParams {
  ratios: number[];
  proportions: string;
  averageRatio: number;
  maxWidth: number;
  spacing: number;
}

function getProportions(ratios: number[]): string {
  return ratios.map((ratio) => (ratio > 1.2 ? 'w' : ratio < 0.8 ? 'n' : 'q')).join('');
}

function getAverageRatio(ratios: number[]): number {
  return ratios.reduce((result, ratio) => ratio + result, 1) / ratios.length;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function cropRatios(ratios: number[], averageRatio: number): number[] {
  return ratios.map((ratio) => {
    return averageRatio > 1.1 ? clamp(ratio, 1, 2.75) : clamp(ratio, 0.6667, 1);
  });
}

function calculateContainerSize(layout: MediaLayout[]): { width: number; height: number } {
  let maxX = 0;
  let maxY = 0;
  layout.forEach(({ dimensions }) => {
    maxX = Math.max(maxX, dimensions.x + dimensions.width);
    maxY = Math.max(maxY, dimensions.y + dimensions.height);
  });
  return { width: maxX, height: maxY };
}

/**
 * Calculate album layout for multiple images
 * @param imageRatios - Array of width/height ratios for each image
 * @param maxWidth - Maximum container width
 */
export function calculateAlbumLayout(imageRatios: number[], maxWidth: number): AlbumLayout {
  const spacing = 2;
  const ratios = imageRatios;
  const proportions = getProportions(ratios);
  const averageRatio = getAverageRatio(ratios);
  const count = ratios.length;

  const params: LayoutParams = {
    ratios,
    proportions,
    averageRatio,
    maxWidth,
    spacing,
  };

  let layout: MediaLayout[];

  if (count >= 5) {
    layout = layoutComplex(params);
  } else if (count === 4) {
    layout = layoutFour(params);
  } else if (count === 3) {
    layout = layoutThree(params);
  } else {
    layout = layoutTwo(params);
  }

  const size = calculateContainerSize(layout);
  return {
    layout,
    containerWidth: size.width,
    containerHeight: size.height,
  };
}

function layoutTwo(params: LayoutParams): MediaLayout[] {
  const { ratios, proportions, averageRatio, maxWidth, spacing } = params;

  // Both wide and similar → stack vertically
  if (proportions === 'ww' && averageRatio > 1.4 && Math.abs(ratios[1] - ratios[0]) < 0.2) {
    const height = Math.round(Math.min(maxWidth / ratios[0], maxWidth / ratios[1], 200));
    return [
      { dimensions: { x: 0, y: 0, width: maxWidth, height } },
      { dimensions: { x: 0, y: height + spacing, width: maxWidth, height } },
    ];
  }

  // Equal split for similar ratios
  if (proportions === 'ww' || proportions === 'qq') {
    const width = Math.round((maxWidth - spacing) / 2);
    const height = Math.round(Math.min(width / ratios[0], width / ratios[1], 300));
    return [
      { dimensions: { x: 0, y: 0, width, height } },
      { dimensions: { x: width + spacing, y: 0, width, height } },
    ];
  }

  // Proportional split
  const width1 = Math.round(((maxWidth - spacing) * ratios[0]) / (ratios[0] + ratios[1]));
  const width2 = maxWidth - width1 - spacing;
  const height = Math.round(Math.min(width1 / ratios[0], width2 / ratios[1], 300));
  return [
    { dimensions: { x: 0, y: 0, width: width1, height } },
    { dimensions: { x: width1 + spacing, y: 0, width: width2, height } },
  ];
}

function layoutThree(params: LayoutParams): MediaLayout[] {
  const { ratios, proportions, maxWidth, spacing } = params;

  // First is wide → 1 on top, 2 below
  if (proportions[0] === 'w') {
    const topHeight = Math.round(Math.min(maxWidth / ratios[0], 200));
    const bottomWidth = Math.round((maxWidth - spacing) / 2);
    const bottomHeight = Math.round(Math.min(bottomWidth / ratios[1], bottomWidth / ratios[2], 150));
    return [
      { dimensions: { x: 0, y: 0, width: maxWidth, height: topHeight } },
      { dimensions: { x: 0, y: topHeight + spacing, width: bottomWidth, height: bottomHeight } },
      { dimensions: { x: bottomWidth + spacing, y: topHeight + spacing, width: bottomWidth, height: bottomHeight } },
    ];
  }

  // 1 on left, 2 on right
  const totalHeight = Math.round(Math.min(maxWidth * 0.8, 350));
  const leftWidth = Math.round(Math.min(totalHeight * ratios[0], maxWidth * 0.6));
  const rightWidth = maxWidth - leftWidth - spacing;
  const rightHeight = Math.round((totalHeight - spacing) / 2);
  return [
    { dimensions: { x: 0, y: 0, width: leftWidth, height: totalHeight } },
    { dimensions: { x: leftWidth + spacing, y: 0, width: rightWidth, height: rightHeight } },
    { dimensions: { x: leftWidth + spacing, y: rightHeight + spacing, width: rightWidth, height: rightHeight } },
  ];
}

function layoutFour(params: LayoutParams): MediaLayout[] {
  const { ratios, proportions, maxWidth, spacing } = params;

  // First is wide → 1 on top, 3 below
  if (proportions[0] === 'w') {
    const topHeight = Math.round(Math.min(maxWidth / ratios[0], 180));
    const bottomWidth = Math.round((maxWidth - 2 * spacing) / 3);
    const bottomHeight = Math.round(Math.min(bottomWidth / ratios[1], bottomWidth / ratios[2], bottomWidth / ratios[3], 120));
    return [
      { dimensions: { x: 0, y: 0, width: maxWidth, height: topHeight } },
      { dimensions: { x: 0, y: topHeight + spacing, width: bottomWidth, height: bottomHeight } },
      { dimensions: { x: bottomWidth + spacing, y: topHeight + spacing, width: bottomWidth, height: bottomHeight } },
      { dimensions: { x: 2 * (bottomWidth + spacing), y: topHeight + spacing, width: bottomWidth, height: bottomHeight } },
    ];
  }

  // 1 on left, 3 on right
  const totalHeight = Math.round(Math.min(maxWidth * 0.8, 350));
  const leftWidth = Math.round(Math.min(totalHeight * ratios[0], maxWidth * 0.55));
  const rightWidth = maxWidth - leftWidth - spacing;
  const rightHeight = Math.round((totalHeight - 2 * spacing) / 3);
  return [
    { dimensions: { x: 0, y: 0, width: leftWidth, height: totalHeight } },
    { dimensions: { x: leftWidth + spacing, y: 0, width: rightWidth, height: rightHeight } },
    { dimensions: { x: leftWidth + spacing, y: rightHeight + spacing, width: rightWidth, height: rightHeight } },
    { dimensions: { x: leftWidth + spacing, y: 2 * (rightHeight + spacing), width: rightWidth, height: rightHeight } },
  ];
}

function layoutComplex(params: LayoutParams): MediaLayout[] {
  const { ratios: originalRatios, averageRatio, maxWidth, spacing } = params;
  const ratios = cropRatios(originalRatios, averageRatio);
  const count = ratios.length;
  const maxHeight = maxWidth;

  // Try different row configurations
  const attempts: { lineCounts: number[]; heights: number[] }[] = [];

  const multiHeight = (offset: number, lineCount: number) => {
    const lineRatios = ratios.slice(offset, offset + lineCount);
    const sum = lineRatios.reduce((acc, r) => acc + r, 0);
    return (maxWidth - (lineCount - 1) * spacing) / sum;
  };

  const pushAttempt = (lineCounts: number[]) => {
    const heights: number[] = [];
    let offset = 0;
    lineCounts.forEach((currentCount) => {
      heights.push(multiHeight(offset, currentCount));
      offset += currentCount;
    });
    attempts.push({ lineCounts, heights });
  };

  // Generate possible row configurations
  for (let first = 1; first <= Math.min(3, count - 1); first++) {
    const second = count - first;
    if (second <= 4) pushAttempt([first, second]);
  }

  for (let first = 1; first <= Math.min(3, count - 2); first++) {
    for (let second = 1; second <= Math.min(3, count - first - 1); second++) {
      const third = count - first - second;
      if (third <= 4) pushAttempt([first, second, third]);
    }
  }

  // Find optimal configuration
  let bestAttempt = attempts[0];
  let bestDiff = Infinity;

  attempts.forEach((attempt) => {
    const totalHeight = attempt.heights.reduce((a, b) => a + b, 0) + (attempt.lineCounts.length - 1) * spacing;
    const diff = Math.abs(totalHeight - maxHeight);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestAttempt = attempt;
    }
  });

  // Fallback for edge cases (e.g., 11+ images where no valid 2-3 row config exists)
  if (!bestAttempt) {
    const cols = Math.min(4, count);
    const rows = Math.ceil(count / cols);
    const lineCounts: number[] = [];
    let remaining = count;
    for (let i = 0; i < rows; i++) {
      const rowCount = Math.min(cols, remaining);
      lineCounts.push(rowCount);
      remaining -= rowCount;
    }
    const rowHeight = (maxWidth - spacing * (cols - 1)) / cols;
    bestAttempt = {
      lineCounts,
      heights: lineCounts.map(() => rowHeight),
    };
  }

  // Build layout from best attempt
  const result: MediaLayout[] = [];
  let index = 0;
  let y = 0;

  bestAttempt.lineCounts.forEach((colCount, row) => {
    const lineHeight = Math.round(bestAttempt.heights[row]);
    let x = 0;

    for (let col = 0; col < colCount; col++) {
      const ratio = ratios[index];
      const isLast = col === colCount - 1;
      const width = isLast ? maxWidth - x : Math.round(ratio * lineHeight);

      result.push({
        dimensions: { x, y, width, height: lineHeight },
      });

      x += width + spacing;
      index++;
    }
    y += lineHeight + spacing;
  });

  return result;
}
